import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {CustomerUserData, UserData} from '@/types/composables/layout/IBaseLayout.js';
import {IImage} from '@/types/components/common/IImage';
import {syncMediaAuthCookie} from '@/common/auth/mediaAuthCookie';
import {clearUserSwitchTarget} from '@/common/auth/userSwitchState';

export const useStore = defineStore('store', () => {
  const userData = ref<UserData | CustomerUserData>();
  const isAdmin = ref<boolean>(false);
  const roles = ref<string[]>([]);
  const accessToken = ref<string | null>(null);
  const listSettings = ref<Record<string, any>>({}); //the list settings per entry
  // Coaching-statistics scope: 'active' (only active coachings, default) or
  // 'all' (every client incl. finished/stale). Shared across statistic views.
  const statisticScope = ref<'active' | 'all'>('active');
  // Trial mode: whether the customer is inside its trial window (used to
  // pre-hide trial-blocked controls) and the latest trial-limit error code
  // pushed by the API interceptor (used to surface a readable message).
  const trialActive = ref<boolean>(false);
  const trialLimitCode = ref<string | null>(null);
  // Post-trial: the window has fully passed and the customer is locked behind
  // the forced expiry screen until we free them. `trialDecision` mirrors the
  // admin's recorded choice ('cancelled' | 'continue' | null).
  const trialLocked = ref<boolean>(false);
  const trialDecision = ref<'cancelled' | 'continue' | null>(null);
  const coaching = ref({
    corporateIdentity: false,
    course: false,
    diary: false,
    recipe: false,
    statistic: false,
    exam: false,
    notification: false,
    liveCoaching: false,
    partnerShops: false,
  });

  // Convenience accessors over the logged-in user's data, used by the
  // dashboard header and the profile image upload.
  const contact = computed(() => userData.value?.contact);

  const image = computed<{name: string | null; mimeType: string | null} | undefined>({
    get: () => userData.value?.image,
    set: (value) => {
      if (userData.value && value) {
        userData.value.image = value as IImage;
      }
    },
  });

  const userLetters = (): string => {
    const currentContact = userData.value?.contact;
    if (!currentContact) {
      return '';
    }
    return `${currentContact.firstname.charAt(0)}${currentContact.lastname.charAt(0)}`.toUpperCase();
  };

  const setUserData = (data: UserData | CustomerUserData) => {

    if (isUserData(data)) {
      roles.value = data.roles;
      isAdmin.value = true;
    } else {
      isAdmin.value = false;
    }
    // Guard against a payload that arrives without customer_modules (e.g. an
    // unexpected/partial body slipping through during a token-refresh retry).
    // Missing modules default to disabled rather than crashing app init with an
    // "undefined is not an object" deref on the whole bootstrap chain.
    const customerModules = data.customer_modules;
    coaching.value.corporateIdentity = customerModules?.corporate_identity ?? false;
    coaching.value.course = customerModules?.course ?? false;
    coaching.value.diary = customerModules?.diary ?? false;
    coaching.value.recipe = customerModules?.recipe ?? false;
    coaching.value.statistic = customerModules?.statistic ?? false;
    coaching.value.exam = customerModules?.exam ?? false;
    coaching.value.notification = customerModules?.notification ?? false;
    coaching.value.liveCoaching = customerModules?.live_coaching ?? false;
    coaching.value.partnerShops = customerModules?.partner_shops ?? false;
    userData.value = {...data};
  };

  const setAccessToken = (token: string | null) => {
    accessToken.value = token;
    // Keep the media-auth cookie in sync so native <img>/<video>/<pdf> requests
    // to /files/… (which can't send a Bearer header) stay authenticated.
    syncMediaAuthCookie(token);
  };

  const setStatisticScope = (scope: 'active' | 'all') => {
    statisticScope.value = scope;
  };

  const setTrialActive = (active: boolean) => {
    trialActive.value = active;
  };

  const setTrialLimitCode = (code: string | null) => {
    trialLimitCode.value = code;
  };

  const setTrialLock = (locked: boolean, decision: 'cancelled' | 'continue' | null) => {
    trialLocked.value = locked;
    trialDecision.value = decision;
  };

  const logout = () => {
    userData.value = undefined;
    roles.value = [];
    accessToken.value = null;
    syncMediaAuthCookie(null);
    clearUserSwitchTarget();
    trialActive.value = false;
    trialLimitCode.value = null;
    trialLocked.value = false;
    trialDecision.value = null;
  };

  const isUserData = (data: UserData | CustomerUserData): data is UserData => {
    return 'roles' in data;
  };

  const isCustomerUserData = (data: UserData | CustomerUserData): data is CustomerUserData => {
    return !('roles' in data);
  };

  return {userData, roles, isAdmin, accessToken, coaching, listSettings, statisticScope, trialActive, trialLimitCode, trialLocked, trialDecision, contact, image, userLetters, logout, setUserData, setAccessToken, setStatisticScope, setTrialActive, setTrialLimitCode, setTrialLock, isUserData, isCustomerUserData};
});
